import React, { Suspense, useEffect } from 'react';
import './globalStyles/global.scss';
import './globalStyles/main.scss';
import './globalStyles/positions-chart.scss';
import './i18n';
import {
    createRoutesFromChildren,
    matchRoutes,
    RouterProvider,
    useLocation,
    useNavigationType,
} from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from './store/store';
import { darkTheme, lightTheme } from './globalStyles/theme';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { selectTheme } from './state/general/generalSelectors';
import InitialLoading from './components/InitialLoading';
import { THEME_VARIANTS } from './state/member/memberTypes';
import logger from './lib/logger';
import CrashSite from './components/ErrorBoundary/CrashSite';
import useAppRouter from '@/router';
import useTinybaseListeners from '@/store/syncStore/hooks/useTinybaseListeners';
import WagmiContext from '@/context/WagmiContext/WagmiContext';

const Sentry = logger.getSentry();

function App() {
    const storedTheme = useSelector((state: RootState) => selectTheme(state));

    const router = useAppRouter();
    // useAnalytics(true);
    // useFirebaseListeners();
    // useSmartlook();
    useTinybaseListeners();
    useEffect(() => {
        const unsubscribe = router.subscribe((state) => {
            const { location } = state;
            Sentry.addBreadcrumb({
                category: 'navigation',
                message: `Navigated to ${location.pathname}`,
                level: 'info',
            });
            Sentry.setTag('current_page', location.pathname);
        });

        logger.init({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        });
        return () => {
            logger
                .close()
                .then(() => {
                    console.info('logger closed');
                })
                .catch((error) => {
                    console.error(error);
                });
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        const root = document.documentElement;
        const color =
            storedTheme === THEME_VARIANTS.LIGHT
                ? lightTheme.palette.text.primary
                : darkTheme.palette.text.primary;
        root.style.setProperty('--text-primary', color);
    }, [storedTheme]);

    return (
        <WagmiContext>
            <ThemeProvider theme={storedTheme === THEME_VARIANTS.LIGHT ? lightTheme : darkTheme}>
                <CssBaseline />
                <Suspense fallback={<InitialLoading />}>
                    {/*<HooksConnectComponent />*/}
                    <Sentry.ErrorBoundary
                        fallback={<CrashSite />}
                        onError={(error, componentStack) => {
                            console.info('************');
                            console.info(error);
                            console.info('stack', componentStack);
                            console.info('************');
                        }}
                    >
                        <RouterProvider router={router} />
                    </Sentry.ErrorBoundary>
                </Suspense>
            </ThemeProvider>
        </WagmiContext>
    );
}

export default Sentry.withProfiler(App);
